// Layout for the ADMIN (operations) login — the expensadmin account.
//
// ONE navigation, not two. The old layout had a top nav AND a four-step flow
// strip repeating the same four destinations on every screen, which left the
// admin unsure which one to use. The strip is gone; its job — showing what is
// waiting — is now a live count on each nav item, and the full flow board
// lives on My Day, where it belongs.
//
// The header is two rows at EVERY size, so nothing has to be squeezed:
//   row 1  brand · cash in hand · logout
//   row 2  the daily flow, left to right
// Only the nav labels shorten on a small screen; the layout never changes
// shape. The step numbers live on the page heading, not in the nav, so the
// nav stays scannable.
//
// Every screen gets the same header: which step it is, what the screen is,
// one line on what it is for, and its actions on the right.

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  FiCreditCard, FiLogOut, FiShoppingBag, FiTruck, FiFileText, FiSun, FiRefreshCw,
} from 'react-icons/fi';
import { capcrmlogo } from '../../assets';
import { logoutExpenses } from '../../redux/slices/expensesSlice';
import { ROUTES } from '../../constants/app.constants';
import { OpsDayProvider, opsCounts, useOpsDay } from '../../hooks/useOpsDay';

// The daily flow, in the order the work actually happens. `badge` names the
// count from opsCounts() that belongs to this step.
export const OPS_NAV = [
  { label: 'My Day', short: 'Day', icon: FiSun, path: ROUTES.OPS_HOME,
    purpose: 'Everything still waiting on you today, and the cash position.' },
  { label: 'Counter Sales', short: 'Counter', icon: FiShoppingBag, step: 1,
    path: ROUTES.OPS_COUNTER, badge: 'toEnter',
    purpose: "Record the day's sales — yours, walk-ins and supervisor sales." },
  { label: 'Payments', short: 'Payments', icon: FiCreditCard, step: 2,
    path: ROUTES.OPS_PAYMENTS, badge: 'toApprove',
    purpose: 'Check what the customer actually paid and approve it — approved orders move on to dispatch.' },
  { label: 'Dispatch', short: 'Dispatch', icon: FiTruck, step: 3,
    path: ROUTES.OPS_DISPATCH, badge: 'toDispatch',
    purpose: 'Hand over paid orders. Dispatch is what completes a sale.' },
  { label: 'Expenses', short: 'Expenses', icon: FiFileText, step: 4,
    path: ROUTES.EXPENSES, badge: 'dueToday',
    purpose: 'Log what went out of the drawer, and settle what the business owes.' },
];

export const opsNavItemFor = (pathname) => OPS_NAV.find((i) => i.path === pathname);

const rupees = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// ─── Header ───────────────────────────────────────────────────────────────────

const OpsHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { day, payables } = useOpsDay();
  const counts = opsCounts(day, payables);
  const balance = Number(day?.balance);

  const handleLogout = async () => {
    await dispatch(logoutExpenses());
    navigate('/login?tab=expenses');
  };

  // ONE nav treatment at every screen size. The old header squeezed five
  // items between the logo and the buttons on desktop, then swapped to a
  // different stacked layout below lg — two things to learn, and the badge
  // sat on top of the icon. Now the nav gets its own full-width row and the
  // only thing that changes on a small screen is the label getting shorter.
  const NavButton = ({ item }) => {
    const active = location.pathname === item.path;
    const waiting = item.badge ? counts[item.badge] : 0;
    const Icon = item.icon;
    return (
      <button
        type="button"
        onClick={() => navigate(item.path)}
        title={item.purpose}
        className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
          active
            ? 'bg-violet-600 text-white shadow-sm shadow-violet-500/25'
            : 'text-slate-500 hover:bg-white hover:text-slate-800'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">{item.label}</span>
        <span className="sm:hidden">{item.short}</span>
        {waiting > 0 && (
          <span className={`inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-black tabular-nums ${
            active ? 'bg-white/25 text-white' : 'bg-amber-500 text-white'
          }`}>
            {waiting > 99 ? '99+' : waiting}
          </span>
        )}
      </button>
    );
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white shadow-sm">
      {/* Row 1 — who you are, what is in the drawer, and the way out */}
      <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
        <button
          type="button"
          onClick={() => navigate(ROUTES.OPS_HOME)}
          className="flex shrink-0 items-center gap-2.5 text-left"
        >
          <img src={capcrmlogo} alt="CAP" className="h-8 w-8 shrink-0 object-contain" />
          <span className="leading-tight">
            <span className="block text-sm font-bold text-slate-900">Admin</span>
            <span className="block text-[11px] text-slate-400">Daily operations</span>
          </span>
        </button>

        <div className="ml-auto flex items-center gap-2">
          {/* Cash in hand — the number the admin answers for at closing time,
              so it stays on screen at every size, not just on desktop. */}
          {Number.isFinite(balance) && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.EXPENSES)}
              title="Money in today, minus refunds handed back and expenses paid"
              className={`rounded-xl border px-3 py-1.5 text-right leading-tight transition-all ${
                balance < 0
                  ? 'border-rose-200 bg-rose-50 hover:bg-rose-100'
                  : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
              }`}
            >
              <span className={`block text-[10px] font-bold uppercase tracking-wide ${
                balance < 0 ? 'text-rose-400' : 'text-emerald-500'
              }`}>
                In hand
              </span>
              <span className={`block text-sm font-black tabular-nums ${
                balance < 0 ? 'text-rose-600' : 'text-emerald-700'
              }`}>
                {rupees(balance)}
              </span>
            </button>
          )}
          <button
            onClick={handleLogout}
            title="Log out"
            className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-500 transition-all hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
          >
            <FiLogOut className="h-4 w-4" />
            <span className="hidden md:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Row 2 — the daily flow, left to right, with what is waiting on each */}
      <div className="border-t border-slate-100 bg-slate-50/80">
        <nav className="mx-auto flex max-w-5xl items-center gap-1 overflow-x-auto px-3 py-1.5">
          {OPS_NAV.map((item) => <NavButton key={item.path} item={item} />)}
        </nav>
      </div>
    </header>
  );
};

// ─── Page shell ───────────────────────────────────────────────────────────────

const OpsPage = ({ title, subtitle, actions, children }) => {
  const location = useLocation();
  const { loading, refresh } = useOpsDay();
  const nav = opsNavItemFor(location.pathname);
  const heading = title || nav?.label || '';
  const line = subtitle !== undefined ? subtitle : nav?.purpose;

  return (
    <main className="mx-auto max-w-5xl px-4 py-6">
      {(heading || line) && (
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {nav?.step && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[11px] font-black text-white">
                  {nav.step}
                </span>
              )}
              <h1 className="text-xl font-black text-slate-900">{heading}</h1>
            </div>
            {line && <p className="mt-1 text-sm text-slate-500">{line}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button
              onClick={refresh}
              disabled={loading}
              title="Refresh today's numbers"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500 transition-all hover:bg-slate-50 disabled:opacity-60"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      )}
      {children}
    </main>
  );
};

const OpsLayout = ({ title, subtitle, actions, children }) => (
  <OpsDayProvider>
    <div className="min-h-screen bg-slate-100">
      <OpsHeader />
      <OpsPage title={title} subtitle={subtitle} actions={actions}>
        {children}
      </OpsPage>
    </div>
  </OpsDayProvider>
);

export default OpsLayout;
